import bind from 'bind-decorator';

import { WebSocketConnection } from './WebSocketConnection';

import { Logger } from '@egr/xbox/utils/errors/Logger';
import { limitConcurrentCalls } from '@egr/xbox/utils/Promise';
import { safelyParseJSON } from '@egr/xbox/utils/Json';

import { Lock } from '@egr/wcf/modules/utils/async';
import { isString } from '@egr/wcf/modules/utils/string';

type ReadCallback = (data: unknown) => void;

interface WriteEntry {
    path: string;
    data: ArrayBuffer | Blob | string;
}

interface ReadEntry {
    path: string;
    binary: boolean;
}

export let wsApi: WebSocketApi | undefined;
export function initializeWebsocket(url: string): void {
    wsApi = new WebSocketApi(url);
}

const requestLock: Lock = new Lock(1);

class WebSocketApi {
    protected readQueue: Array<ReadCallback> = [];
    protected connection: WebSocketConnection;

    public constructor(protected url: string) {
        this.connection = new WebSocketConnection(
            url,
            this.handleMessage
        );
    }

    @limitConcurrentCalls(requestLock)
    public async writeFiles(entries: Array<WriteEntry>): Promise<void> {
        const files: Array<string> = entries.map((entry: WriteEntry): string => entry.path);
        const promise: Promise<string> = this.sendRequest({
            action: 'write',
            arguments: {
                paths: files
            }
        });

        for (const entry of entries) {
            if (entry.data instanceof ArrayBuffer || entry.data instanceof Blob) {
                await this.connection.send(entry.data);
            } else {
                await this.connection.send('D' + entry.data);
            }
        }

        await promise;
    }

    @limitConcurrentCalls(requestLock)
    public async readFiles(paths: Array<ReadEntry>): Promise<Array<ArrayBuffer | string>> {
        const callbacks: Array<Promise<ArrayBuffer | string>> = paths.map((): Promise<ArrayBuffer | string> => {
            return new Promise<ArrayBuffer | string>((resolve: (data: ArrayBuffer | string) => void, reject: (error: Error) => void): void => {
                this.readQueue.push((response: unknown): void => {
                    if (isString(response)) {
                        if (response[0] === 'D') {
                            resolve(response.slice(1));
                            return;
                        }

                        if (response[0] === 'E') {
                            this.readQueue.length = 0; // FixMe
                            reject(new Error(response.slice(1)));
                            return;
                        }
                    }
                    resolve(response as ArrayBuffer);
                });
            });
        });

        const promise: Promise<string> = this.sendRequest({
            action: 'read',
            arguments: {
                paths
            }
        });

        const data: Array<ArrayBuffer | string> = await Promise.all(callbacks);

        await promise;

        return data;
    }

    @limitConcurrentCalls(requestLock)
    public async list(path: string, maxdepth: number = 1): Promise<Array<string>> {
        const reply: string = await this.sendRequest({
            action: 'list',
            arguments: {
                path,
                maxdepth
            }
        });

        return safelyParseJSON(reply) ?? [];
    }

    @limitConcurrentCalls(requestLock)
    public async remove(paths: Array<string>): Promise<void> {
        await this.sendRequest({
            action: 'remove',
            arguments: {
                paths
            }
        });
    }

    @limitConcurrentCalls(requestLock)
    public async copy(source: string, destination: string, override: boolean): Promise<void> {
        await this.sendRequest({
            action: 'copy',
            arguments: {
                source,
                destination,
                override
            }
        });
    }

    @limitConcurrentCalls(requestLock)
    public async size(path:  string): Promise<number> {
        const size: string = await this.sendRequest({
            action: 'size',
            arguments: {
                path
            }
        });
        return parseInt(size, 10);
    }

    /**
     * Note: add the read callbacks to the `readQueue` before calling this function
     */
    protected sendRequest(data: {action: string, arguments: unknown}): Promise<string> {
        // eslint-disable-next-line no-async-promise-executor
        return new Promise<string>(async (
            resolve: (data: string) => void,
            reject: (error: Error) => void
        ): Promise<void> => {
            try {
                // Note: .send may throw an error
                await this.connection.send('R' + JSON.stringify(data));
                this.readQueue.push((response: unknown): void => {
                    if (isString(response)) {
                        if (response[0] === 'S') {
                            resolve(response.slice(1));
                            return;
                        }

                        if (response[0] === 'E') {
                            reject(new Error(response.slice(1)));
                            return;
                        }
                    }
                    reject(new Error('invalid response'));
                });
            } catch (error) {
                reject(error);
            }
        });
    }

    @bind
    protected handleMessage(event: MessageEvent): void {
        const callback: ReadCallback | undefined = this.readQueue.shift();
        if (callback != null) {
            if (event.data instanceof ArrayBuffer) {
                callback(event.data);
            } else {
                callback(event.data[0] === 'D' ? event.data.slice(1) : event.data);
            }
        } else {
            Logger.warn('Missing callback for websocket event');
        }
    }
}
